import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Products from '../components/Products';

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.name.trim()) {
      alert('Please enter your full name');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert('Please enter a valid email address');
      return;
    }
    
    if (!formData.message.trim()) {
      alert('Please enter your message');
      return;
    }
    
    setIsSending(true);
    
    try {
      // Call your PHP backend
      const response = await fetch('https://salstechitsolutions.com/api/send-contact-email.php', {
      // const response = await fetch('http://localhost/SalsTech-IT-Solutions/api/send-contact-email.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      const result = await response.json();
      
      if (result.success) {
        alert('✅ Message sent successfully! Check your email for confirmation. We\'ll get back to you within 24 hours.');
        setFormData({ name: '', email: '', message: '' });
      } else {
        alert('❌ Sorry, there was an error: ' + (result.error || 'Please try again'));
      }
    } catch (error) {
      // console.error('Error:', error);
      alert('❌ Network error. Please check your connection and try again.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrollToSection={scrollToSection} />

      {/* Hero Section with Background Image */}
      <section id="home" className="relative pt-16 md:pt-20 min-h-screen flex items-center overflow-hidden">
        {/* Background with overlay */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-sals-dark via-sals-primary to-sals-light opacity-95"></div>
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3')] bg-cover bg-center opacity-60"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl mx-auto text-center">
            <div className="text-white animate-fade-in">
              <div className="mb-4 inline-block px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm">
                🇳🇬 Nigeria's Trusted Tech Partner
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                Building Digital <span className="text-sals-light">Excellence</span>
              </h1>
              <p className="text-lg md:text-xl mb-8 opacity-90 leading-relaxed">
                SalsTech IT Solutions delivers innovative software, web applications, and professional web development training. Let's bring your ideas to life.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <button
                  onClick={() => scrollToSection('services')}
                  className="bg-white text-sals-dark px-8 py-3 rounded-lg font-semibold hover:bg-sals-light hover:text-white transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  Explore Services
                </button>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-sals-dark transition-all duration-300"
                >
                  Contact Us
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
            <div className="w-1 h-2 bg-white rounded-full mt-2 animate-scroll"></div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-sals-dark mb-4">
              Our <span className="text-sals-primary">Services</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-sals-primary to-sals-light mx-auto rounded-full"></div>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Comprehensive tech solutions tailored to your business needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: 'Software Development',
                description: 'Custom enterprise software solutions built with cutting-edge technology.',
                icon: '💻',
                color: 'from-sals-dark to-sals-primary',
                features: ['Custom Solutions', 'Scalable Architecture', 'API Integration']
              },
              {
                title: 'Web Applications',
                description: 'Dynamic, scalable web apps that drive business growth.',
                icon: '🌐',
                color: 'from-sals-primary to-sals-light',
                features: ['Responsive Design', 'Real-time Features', 'Cloud Ready']
              },
              {
                title: 'Websites',
                description: 'Responsive, SEO-friendly websites that make a lasting impression.',
                icon: '🎨',
                color: 'from-sals-light to-sals-primary',
                features: ['SEO Optimized', 'Mobile First', 'Fast Loading']
              },
              {
                title: 'Web Dev Training',
                description: 'Comprehensive training for aspiring developers.',
                icon: '📚',
                color: 'from-sals-dark to-sals-light',
                features: ['Hands-on Projects', 'Expert Mentors', 'Job Ready']
              }
            ].map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg overflow-hidden transform hover:-translate-y-2 transition-all duration-300 group"
              >
                <div className={`bg-gradient-to-r ${service.color} p-6 text-center relative overflow-hidden`}>
                  <div className="relative z-10">
                    <div className="text-5xl mb-3 group-hover:scale-110 transition-transform duration-300">{service.icon}</div>
                    <h3 className="text-xl font-bold text-white">{service.title}</h3>
                  </div>
                  <div className="absolute inset-0 bg-white/10 transform -skew-x-12 translate-x-full group-hover:translate-x-0 transition-transform duration-500"></div>
                </div>
                <div className="p-6">
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-600">
                        <svg className="w-4 h-4 text-sals-primary mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <Products />

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-sals-dark mb-6">
                About <span className="text-sals-primary">SalsTech</span>
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-sals-primary to-sals-light mb-6 rounded-full"></div>
              <p className="text-gray-700 mb-4 leading-relaxed">
                SalsTech IT Solutions is a premier technology company registered with the Corporate Affairs Commission (CAC) of Nigeria. We specialize in building innovative software solutions, web applications, and professional websites that drive business success.
              </p>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Our commitment to excellence and continuous learning has made us a trusted partner for businesses across Nigeria. We also empower the next generation of developers through our comprehensive web development training programs.
              </p>
              <div className="flex items-center space-x-2 text-sals-primary mb-4">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="font-semibold">Registered Company - RC: 7398490</span>
              </div>
              {/* <div className="flex gap-4">
                <button className="px-6 py-2 bg-sals-primary text-white rounded-lg hover:bg-sals-dark transition-colors">
                  Learn More
                </button>
                <button className="px-6 py-2 border-2 border-sals-primary text-sals-primary rounded-lg hover:bg-sals-primary hover:text-white transition-colors">
                  Our Team
                </button>
              </div> */}
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-sals-dark to-sals-primary rounded-2xl p-8 text-white transform hover:scale-105 transition-transform duration-300 shadow-2xl">
                <h3 className="text-2xl font-bold mb-4">Why Choose Us?</h3>
                <ul className="space-y-3">
                  {[
                    '✅ Expert Team of Developers',
                    '✅ Cutting-Edge Technologies',
                    '✅ Timely Project Delivery',
                    '✅ 24/7 Customer Support',
                    '✅ Affordable & Transparent Pricing',
                    '✅ Post-Launch Maintenance'
                  ].map((item, i) => (
                    <li key={i} className="flex items-center">
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="absolute -top-4 -right-4 w-32 h-32 bg-sals-light rounded-full opacity-20 blur-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-sals-dark mb-4">
              Get In <span className="text-sals-primary">Touch</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-sals-primary to-sals-light mx-auto rounded-full"></div>
            <p className="text-gray-600 mt-4">Let's discuss your next project or training needs</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    disabled={isSending}  // Add this
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sals-primary focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sals-primary focus:border-transparent transition-all"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Message</label>
                  <textarea
                    rows="5"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sals-primary focus:border-transparent transition-all"
                    placeholder="Tell us about your project..."
                  ></textarea>
                </div>
                <button
                    type="submit"
                    disabled={isSending}
                    className="w-full bg-gradient-to-r from-sals-primary to-sals-light text-white py-3 rounded-lg font-semibold hover:from-sals-dark hover:to-sals-primary transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    {isSending ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </span>
                    ) : (
                      'Send Message'
                    )}
                  </button>
              </form>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h3 className="text-2xl font-bold text-sals-dark mb-6">Contact Information</h3>
                <div className="space-y-6">
                  <div className="flex items-center space-x-4 group cursor-pointer">
                    <div className="w-12 h-12 bg-sals-light/20 rounded-full flex items-center justify-center group-hover:bg-sals-primary transition-colors duration-300">
                      <svg className="w-6 h-6 text-sals-primary group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Visit Us</p>
                      <p className="text-gray-600">Elegushi Modern Market, Lagos, Nigeria</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4 group cursor-pointer">
                    <div className="w-12 h-12 bg-sals-light/20 rounded-full flex items-center justify-center group-hover:bg-sals-primary transition-colors duration-300">
                      <svg className="w-6 h-6 text-sals-primary group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Email Us</p>
                      <p className="text-gray-600">salstechmedia@gmail.com</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4 group cursor-pointer">
                    <div className="w-12 h-12 bg-sals-light/20 rounded-full flex items-center justify-center group-hover:bg-sals-primary transition-colors duration-300">
                      <svg className="w-6 h-6 text-sals-primary group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Call Us</p>
                      <p className="text-gray-600">+234 817 139 6978</p>
                      <p className="text-gray-500 text-sm">Mon-Fri, 9am-6pm</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-sals-dark to-sals-primary rounded-2xl p-8 text-white text-center transform hover:scale-105 transition-transform duration-300 shadow-xl">
                <p className="text-2xl font-bold mb-2">Ready to start your project?</p>
                <p className="mb-4 opacity-90">Get a free consultation today</p>
                <button className="bg-white text-sals-dark px-6 py-2 rounded-lg font-semibold hover:bg-sals-light hover:text-white transition-colors">
                  Schedule Meeting
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer scrollToSection={scrollToSection} />

      {/* Custom Animations */}
      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes scroll {
          0% {
            opacity: 1;
            transform: translateY(0);
          }
          100% {
            opacity: 0;
            transform: translateY(10px);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.8s ease-out;
        }
        
        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }
        
        .animate-scroll {
          animation: scroll 1.5s infinite;
        }
        
        .animate-bounce {
          animation: bounce 2s infinite;
        }
        
        @keyframes bounce {
          0%, 100% {
            transform: translateY(-25%);
            animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
          }
          50% {
            transform: translateY(0);
            animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
          }
        }
      `}</style>
    </div>
  );
};

export default Home;